import type { TLShape, TLShapeId } from "@tldraw/tlschema";
import type { VecLike } from "./canvas";
import {
  Idle,
  PointingCanvas,
  PointingShape,
  SelectTool,
} from "./canvas/engine/select-tool";
import {
  StateNode,
  type ClickEventInfo,
  type PointerEventInfo,
} from "./canvas/engine/state-node";
import type { Sheet } from "../shared/schema";
import { sheetAt } from "./sheet-operations";
import { addTextAt } from "./quick-add";

// Planroom's sheet selection rules on top of the engine's select tool.

/** Selection and movement are separate presses, including grouped contents. */
class SelectBeforeMove extends PointingShape {
  override onPointerMove(info: PointerEventInfo) {
    if (this.wasSelected) super.onPointerMove(info);
  }
}

/** The engine sends a double-click while the second press is held; replay it once idle. */
class PointingBlankCanvas extends PointingCanvas {
  private doubleClick?: ClickEventInfo;
  override onEnter(info: PointerEventInfo) {
    this.doubleClick = undefined;
    super.onEnter(info);
  }
  override onDoubleClick(info: ClickEventInfo) {
    if (info.phase === "down") this.doubleClick = info;
  }
  override onPointerUp() {
    super.onPointerUp();
    if (this.doubleClick && this.parent.getCurrent()?.id === "idle")
      this.parent.getCurrent()!.handleEvent(this.doubleClick);
  }
}

/** Click selects paper; dragging its interior brushes contents. Locked items remain inspectable. */
class PointingPaper extends StateNode {
  static override id = "pointing_paper";
  private target?: TLShapeId;
  private canBrush = false;
  private doubleClick?: ClickEventInfo;
  override onEnter(
    info: PointerEventInfo & { paperId: TLShapeId; canBrush?: boolean },
  ) {
    this.target = info.paperId;
    this.canBrush = info.canBrush ?? false;
    this.doubleClick = undefined;
  }
  override onDoubleClick(info: ClickEventInfo) {
    if (info.phase === "down") this.doubleClick = info;
  }
  override onPointerMove(info: PointerEventInfo) {
    if (this.canBrush && this.editor.inputs.getIsDragging())
      this.parent.transition("brushing", { ...info, target: "canvas" });
  }
  override onPointerUp(info: PointerEventInfo) {
    if (this.target) {
      if (info.shiftKey) {
        const ids = this.editor.getSelectedShapeIds();
        this.editor.setSelectedShapes(
          ids.includes(this.target)
            ? ids.filter((id) => id !== this.target)
            : [...ids, this.target],
        );
      } else this.editor.select(this.target);
    }
    this.parent.transition("idle");
    // A double-click on paper arrives mid-press; replay it once idle.
    if (this.doubleClick && !info.shiftKey)
      this.parent.getCurrent()?.handleEvent(this.doubleClick);
  }
  override onCancel() {
    this.parent.transition("idle");
  }
  override onInterrupt() {
    this.parent.transition("idle");
  }
}

class SheetSelectIdle extends Idle {
  override onDoubleClick(info: ClickEventInfo) {
    if (info.phase !== "down" || info.shiftKey) return;
    const editor = this.editor;
    // A press on a selected sheet targets the selection; treat it as the sheet.
    const onlySelected =
      info.target === "selection" ? editor.getOnlySelectedShape() : undefined;
    const onPaperOrCanvas =
      info.target === "canvas" ||
      (info.target === "shape" && info.shape.type === "sheet") ||
      onlySelected?.type === "sheet";
    if (!onPaperOrCanvas) {
      super.onDoubleClick(info);
      return;
    }
    const point = editor.inputs.getCurrentPagePoint();
    // Existing text and embeds on the paper keep their double-click.
    const hit = editor.getShapeAtPoint(point, {
      hitInside: false,
      hitLabels: true,
    });
    if (hit && hit.type !== "sheet") {
      super.onDoubleClick({ ...info, target: "shape", shape: hit });
      return;
    }
    if (editor.getIsReadonly()) return;
    // Anywhere else starts text at the point; locked sheets take none.
    if (sheetAt(editor, point) || !anySheetAt(editor, point))
      addTextAt(editor, point);
  }
  override onPointerDown(info: PointerEventInfo) {
    if (
      info.target === "canvas" ||
      info.target === "shape" ||
      info.target === "selection"
    ) {
      if (info.target === "selection" && info.handle !== undefined) {
        super.onPointerDown(info);
        return;
      }
      const point = this.editor.inputs.getCurrentPagePoint();
      const margin = this.editor.getHitTestMargin();
      // Contents are hit with the canvas's margin, so thin strokes and hollow
      // outlines can be grabbed on paper.
      const hit = this.editor.getShapeAtPoint(point, {
        hitInside: false,
        hitLabels: true,
        hitLocked: true,
        hitFrameInside: true,
        margin,
      });
      // A press on paper inside the selection box of selected contents moves
      // them, as on the bare canvas; only a selected sheet's paper brushes.
      const onPaper =
        !hit ||
        (this.editor.isShapeFrameLike(hit) &&
          insidePaper(this.editor, hit, point, margin));
      if (onPaper && this.insideContentSelection(point)) {
        super.onPointerDown(
          info.target === "selection"
            ? info
            : ({
                ...info,
                target: "selection",
                shape: undefined,
                handle: undefined,
              } as PointerEventInfo),
        );
        return;
      }
      if (hit && this.editor.isShapeOrAncestorLocked(hit)) {
        let locked = hit;
        while (!locked.isLocked && locked.parentId.startsWith("shape:")) {
          const parent = this.editor.getShape(locked.parentId as TLShapeId);
          if (!parent) break;
          locked = parent;
        }
        this.parent.transition("pointing_paper", {
          ...info,
          paperId: locked.id,
        });
        return;
      }
      const sheet = sheetAt(this.editor, point);
      if (sheet && (!hit || hit.id === sheet.id)) {
        const local = this.editor.getPointInShapeSpace(sheet, point),
          margin = this.editor.getHitTestMargin();
        if (
          local.x > margin &&
          local.y > margin &&
          local.x < sheet.props.w - margin &&
          local.y < sheet.props.h - margin
        ) {
          this.parent.transition("pointing_paper", {
            ...info,
            paperId: sheet.id,
            canBrush: true,
          });
          return;
        }
      }
    }
    super.onPointerDown(info);
  }
  /** Whether a page point lies in the selection box of selected contents (not only sheets). */
  private insideContentSelection(point: VecLike) {
    const selected = this.editor.getSelectedShapes();
    if (!selected.length || selected.every((s) => s.type === "sheet"))
      return false;
    const bounds = this.editor.getSelectionPageBounds();
    return !!bounds && bounds.containsPoint(point);
  }
}

/** Any sheet under the point, including locked ones that take no new content. */
function anySheetAt(editor: StateNode["editor"], point: VecLike) {
  return editor.getCurrentPageShapes().some((s) => {
    if (s.type !== "sheet") return false;
    const p = editor.getPointInShapeSpace(s, point);
    const { w, h } = (s as Sheet).props;
    return p.x >= 0 && p.y >= 0 && p.x <= w && p.y <= h;
  });
}

/** Inside a frame's paper, clear of the edge and title that select the frame itself. */
function insidePaper(
  editor: StateNode["editor"],
  frame: TLShape,
  point: VecLike,
  margin: number,
) {
  const local = editor.getPointInShapeSpace(frame, point);
  const bounds = editor.getShapeGeometry(frame).bounds;
  return (
    local.x > bounds.minX + margin &&
    local.y > bounds.minY + margin &&
    local.x < bounds.maxX - margin &&
    local.y < bounds.maxY - margin
  );
}

export class SheetSelectTool extends SelectTool {
  static override children = () =>
    [
      ...SelectTool.children().map((state) =>
        state.id === "idle"
          ? SheetSelectIdle
          : state.id === "pointing_shape"
            ? SelectBeforeMove
            : state.id === "pointing_canvas"
              ? PointingBlankCanvas
              : state,
      ),
      PointingPaper,
    ] as (typeof StateNode & { id: string })[];
}
